// Written by Codex on 2026-10-16
// User prompt: Continue Clara confirmation, notice review and demo label cleanup.
// Written by Claude Code on 2026-05-14
// Written by Codex on 2026-07-29
// User prompt: help
// User prompt: AI-native HRIS — Pillar 1: Natural Language Action Interface

// Written by Codex on 2026-10-15
// User prompt: when you open clara, even if you dont type in the chat it saves the chat.
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { chatbotAPI } from '../services/api';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const activeId = useRef(null);
  const creating = useRef(null);
  const sendInFlight = useRef(false);

  // Initialize conversation once Firebase auth is ready
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        activeId.current = null;
        setConversationId(null);
        setMessages([]);
        setInitialized(false);
        return;
      }

      const storageKey = `chat_conversation_${user.uid}`;
      const savedId = sessionStorage.getItem(storageKey);

      if (savedId) {
        try {
          const res = await chatbotAPI.getMessages(savedId);
          activeId.current = savedId;
          setConversationId(savedId);
          setMessages(res.data.messages.map((m) => ({
            id: m.id,
            role: m.role === 'assistant' ? 'bot' : m.role,
            content: m.content, proposal: m.proposal,
          })));
          setInitialized(true);
          return;
        } catch {
          sessionStorage.removeItem(storageKey);
        }
      }

      activeId.current = null;
      setConversationId(null);
      setMessages([]);
      setInitialized(true);
    });

    return unsubscribe;
  }, []);

  // Opening Clara starts a local draft. Persist only on an explicit send.
  const ensureConversation = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Please sign in to chat.');
    if (activeId.current) return activeId.current;
    if (!creating.current) {
      creating.current = chatbotAPI.createConversation().then(({ data }) => {
        if (auth.currentUser?.uid !== user.uid) throw new Error('Your account changed. Please try again.');
        activeId.current = data.conversationId;
        sessionStorage.setItem(`chat_conversation_${user.uid}`, data.conversationId);
        setConversationId(data.conversationId);
        return data.conversationId;
      }).finally(() => { creating.current = null; });
    }
    return creating.current;
  }, []);

  // Written by Codex on 2026-10-16
  // User prompt: Continue Clara confirmation and duplicate-submit protection.
  const refreshMessages = useCallback(async () => {
    if (!activeId.current) return;
    const id = activeId.current;
    const { data } = await chatbotAPI.getMessages(id);
    if (id === activeId.current) setMessages(data.messages.map(m => ({ ...m, role: m.role === 'assistant' ? 'bot' : m.role })));
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!initialized || !text.trim() || sendInFlight.current) return;
    sendInFlight.current = true;

    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);
    setLastAction(null);

    try {
      const id = await ensureConversation();
      const res = await chatbotAPI.sendMessage(id, text.trim());
      const { message, actionTaken } = res.data;
      const botMsg = { id: `b-${Date.now()}`, role: 'bot', content: message };
      setMessages((prev) => [...prev, botMsg]);
      if (actionTaken) setLastAction(actionTaken);
      if (res.data.proposal_available) await refreshMessages();
      return message;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errorMsg = status === 429
        ? 'Rate limit reached. Please wait a moment and try again.'
        : detail || "I'm having trouble connecting right now. Please try again in a moment.";
      setMessages((prev) => [...prev, { id: `e-${Date.now()}`, role: 'bot', content: errorMsg }]);
      return errorMsg;
    } finally {
      sendInFlight.current = false;
      setSending(false);
    }
  }, [ensureConversation, initialized, refreshMessages]);

  const clearConversation = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || creating.current || sendInFlight.current) return;

    if (conversationId) {
      try { await chatbotAPI.deleteConversation(conversationId); } catch { /* non-fatal */ }
    }

    sessionStorage.removeItem(`chat_conversation_${user.uid}`);
    activeId.current = null;
    setConversationId(null);
    setMessages([]);
    setLastAction(null);
  }, [conversationId]);

  // Switch to an existing conversation (e.g. from history list)
  const switchConversation = useCallback(async (id) => {
    const user = auth.currentUser;
    if (!user || id === conversationId || creating.current || sendInFlight.current) return;
    try {
      const res = await chatbotAPI.getMessages(id);
      activeId.current = id;
      setConversationId(id);
      setMessages(res.data.messages.map((m) => ({
        id: m.id,
        role: m.role === 'assistant' ? 'bot' : m.role,
        content: m.content, proposal: m.proposal,
      })));
      setLastAction(null);
      sessionStorage.setItem(`chat_conversation_${user.uid}`, id);
    } catch { /* non-fatal */ }
  }, [conversationId]);

  // Start a fresh conversation without deleting the current one
  const newConversation = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || creating.current || sendInFlight.current) return;
    sessionStorage.removeItem(`chat_conversation_${user.uid}`);
    activeId.current = null;
    setConversationId(null);
    setMessages([]);
    setLastAction(null);
  }, []);

  return (
    <ChatContext.Provider value={{ conversationId, messages, sending, initialized, lastAction, sendMessage, refreshMessages, ensureConversation, clearConversation, switchConversation, newConversation }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside ChatProvider');
  return ctx;
}
